import { action, makeObservable, observable, runInAction } from 'mobx';
import { notificationService } from '../../services';
import {
  ApiError,
  BuyerNotification,
  NotificationQuery,
} from '../../types';

const DEFAULT_TAKE = 30;

export class NotificationInboxStore {
  items: BuyerNotification[] = [];
  total = 0;
  unreadCount = 0;
  query: NotificationQuery = { skip: 0, take: DEFAULT_TAKE, unreadOnly: false };
  loading = false;
  error: ApiError | null = null;
  mutating = false;

  constructor() {
    makeObservable(this, {
      items: observable,
      total: observable,
      unreadCount: observable,
      query: observable,
      loading: observable,
      error: observable,
      mutating: observable,
      setQuery: action,
      load: action,
      refreshUnreadCount: action,
      markRead: action,
      markAllRead: action,
      reset: action,
    });
  }

  setQuery(patch: Partial<NotificationQuery>): void {
    const next: NotificationQuery = { ...this.query, ...patch };
    if (!('skip' in patch)) next.skip = 0;
    this.query = next;
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const res = await notificationService.list(this.query);
      runInAction(() => {
        this.items = res.items;
        this.total = res.total;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async refreshUnreadCount(): Promise<void> {
    try {
      const count = await notificationService.unreadCount();
      runInAction(() => {
        this.unreadCount = count;
      });
    } catch {
      // Silent — badge is non-critical.
    }
  }

  async markRead(id: string): Promise<void> {
    this.mutating = true;
    try {
      const updated = await notificationService.markRead(id);
      runInAction(() => {
        const idx = this.items.findIndex((n) => n.id === id);
        if (idx >= 0) this.items[idx] = updated;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      });
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
    } finally {
      runInAction(() => {
        this.mutating = false;
      });
    }
  }

  async markAllRead(): Promise<void> {
    this.mutating = true;
    try {
      await notificationService.markAllRead();
      const now = new Date().toISOString();
      runInAction(() => {
        this.items = this.items.map((n) => (n.readAt ? n : { ...n, readAt: now }));
        this.unreadCount = 0;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
    } finally {
      runInAction(() => {
        this.mutating = false;
      });
    }
  }

  reset(): void {
    this.items = [];
    this.total = 0;
    this.unreadCount = 0;
    this.query = { skip: 0, take: DEFAULT_TAKE, unreadOnly: false };
    this.error = null;
  }
}
